import { isQuoteDefinitionBound } from './businessFieldDirectory';
import {
  activeRoleIdForState,
  quotePermissionContext,
  resolveRecursiveEnterpriseFieldGrant,
} from './permissionPolicy';
import { isQuoteFieldAvailable } from './quoteFieldAvailability';
import {
  canProjectGovernedQuoteField,
  type DisposableFieldPermissionContext,
  effectiveQuoteFieldDefinitions,
} from './quoteFieldPolicy';
import {
  normalizeFieldSelection,
  quoteFieldKeysForPlacement,
} from './quoteFieldSelection';
import type {
  CpqRole,
  CpqState,
  Quote,
  QuoteColumnSnapshot,
  QuoteFieldDefinitionRecord,
  QuoteFieldPlacement,
} from './types';
import { createUnifiedFieldRegistry } from './unifiedFieldRegistry';

/** Current-state projection gate.  Pool metadata authorizes visibility but
 * never replaces frozen non-draft snapshot metadata. */
export function visibleQuoteColumnsForState(
  state: CpqState,
  quote: Quote,
  placement: QuoteFieldPlacement = 'quote-line',
): QuoteColumnSnapshot[] {
  const definitions = new Map(
    state.catalog.quoteFieldDefinitions.map((field) => [field.key, field]),
  );
  const bindings = new Map(
    createUnifiedFieldRegistry(state.catalog.quoteFieldDefinitions).map(
      ({ definition, binding }) => [definition.key, binding],
    ),
  );
  const pooledDefinitions = state.catalog.quoteFieldPool
    .filter((entry) => {
      const binding = bindings.get(entry.fieldKey);
      return (
        entry.enabled &&
        entry.placement === placement &&
        isQuoteDefinitionBound(entry.fieldKey) &&
        binding?.poolAllowed === true &&
        binding.quotePlacement === entry.placement
      );
    })
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .flatMap((entry) => {
      const definition = definitions.get(entry.fieldKey);
      return definition ? [{ ...definition, label: entry.displayLabel }] : [];
    });
  const roleId = activeRoleIdForState(state);
  const context = quotePermissionContext(state, quote);
  return visibleQuoteColumns(
    quote,
    pooledDefinitions,
    state.role,
    state,
    placement,
  ).filter(
    (field) =>
      roleId !== undefined &&
      resolveRecursiveEnterpriseFieldGrant(
        state,
        roleId,
        `definition:${field.key}`,
        'view',
        quote.status === 'draft' ? 'draft' : 'submitted',
        context,
      ),
  );
}

export function toQuoteColumnSnapshot(
  field: QuoteFieldDefinitionRecord,
): QuoteColumnSnapshot {
  return {
    key: field.key,
    label: field.label,
    type: field.type,
    width: field.width,
    note: field.note,
    options: field.options?.map((option) => ({ ...option })),
    sourceKind: field.sourceKind,
    fieldKey: field.governance.fieldKey,
    definitionVersion: field.governance.definitionVersion,
    formulaVersion: field.governance.formulaVersion,
    ...(field.calculation
      ? { calculation: structuredClone(field.calculation) }
      : {}),
  };
}

export function visibleQuoteColumns(
  quote: Quote,
  definitions: QuoteFieldDefinitionRecord[],
  role: CpqRole = 'admin',
  permissionContext?: DisposableFieldPermissionContext,
  placement: QuoteFieldPlacement = 'quote-line',
): QuoteColumnSnapshot[] {
  if (quote.status !== 'draft') {
    const seen = new Set<string>();
    return (quote.customColumnSnapshot ?? []).filter((field) => {
      if ((field.placement ?? 'quote-line') !== placement) return false;
      if (seen.has(field.key)) return false;
      seen.add(field.key);
      // Historical raw snapshots remain stored facts, but current projection
      // is definition-governed and fails closed for unapproved fields.
      const definition = definitions.find((item) => item.key === field.key);
      const definitionFrozen =
        field.fieldKey === field.key &&
        typeof field.definitionVersion === 'string' &&
        /^definition-v[1-9]\d*$/.test(field.definitionVersion) &&
        Number.isSafeInteger(
          Number(field.definitionVersion.slice('definition-v'.length)),
        );
      const formulaFrozen =
        definition?.governance.kind !== 'system-calculated' ||
        Boolean(field.formulaVersion);
      return (
        definitionFrozen &&
        formulaFrozen &&
        Boolean(
          canProjectGovernedQuoteField(
            role,
            quote,
            field.key,
            definitions,
            permissionContext,
          ),
        )
      );
    });
  }
  const effectiveDefinitions = effectiveQuoteFieldDefinitions(
    role,
    quote,
    definitions,
    permissionContext,
  );
  const configured = new Map(
    effectiveDefinitions.map((field) => [field.key, field]),
  );
  return normalizeFieldSelection(
    quoteFieldKeysForPlacement(quote, placement),
    effectiveDefinitions,
  ).flatMap((key) => {
    const field = configured.get(key);
    return field ? [toQuoteColumnSnapshot(field)] : [];
  });
}

export function snapshotQuoteColumns(
  keys: string[],
  definitions: QuoteFieldDefinitionRecord[],
  role: CpqRole = 'admin',
  quote?: Quote,
  permissionContext?: DisposableFieldPermissionContext,
): QuoteColumnSnapshot[] {
  const effectiveDefinitions = quote
    ? effectiveQuoteFieldDefinitions(
        role,
        quote,
        definitions,
        permissionContext,
      )
    : definitions;
  const configured = new Map(definitions.map((field) => [field.key, field]));
  const effectiveKeys = new Set(
    normalizeFieldSelection(keys, effectiveDefinitions),
  );
  // Unsafe field contracts are frozen.  A legacy snapshot is a historical
  // fact, not a request to recreate its label, type, or other metadata from
  // the current Mock catalogue.
  const preservedUnavailableSnapshots = (
    quote?.customColumnSnapshot ?? []
  ).filter(
    (field) =>
      quote !== undefined &&
      !canProjectGovernedQuoteField(
        role,
        quote,
        field.key,
        definitions,
        permissionContext,
      ),
  );
  const snapshottedKeys = new Set(
    preservedUnavailableSnapshots.map((field) => field.key),
  );
  const generatedSnapshots = keys.flatMap((key) => {
    const field = configured.get(key);
    if (snapshottedKeys.has(key)) return [];
    if (
      !field ||
      (!effectiveKeys.has(key) && isQuoteFieldAvailable(key, definitions))
    ) {
      return [];
    }
    snapshottedKeys.add(key);
    return [toQuoteColumnSnapshot(field)];
  });
  return [...preservedUnavailableSnapshots, ...generatedSnapshots];
}
